import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Handshake, Plus, Copy, RefreshCw, Pencil, ChevronDown, ChevronRight, Upload } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton, Spinner } from '@/components/ui/misc';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';

interface Partner {
  id: string;
  slug: string;
  name: string;
  logo_url: string;
  secret_prefix: string;
  allowed_origins: string[];
  webhook_url: string;
  webhook_secret_set: boolean;
  key_scopes: string[];
  active: boolean;
  discount_pct: number;
  created_at: string;
}
interface Stats { connections_total: number; connections_active: number; connections_pending: number }
interface Row { partner: Partner; stats: Stats }
interface Conn {
  id: string; external_id: string; status: string; account_id?: string;
  customer: { name?: string; email?: string; company?: string }; created_at: string;
}

const ALL_SCOPES = ['instances:read', 'instances:write', 'messages:send', 'contacts:check', 'presence:write', 'media:read'];

type Form = { name: string; slug: string; origins: string; webhook_url: string; scopes: string[]; active: boolean; discount: string };
const emptyForm: Form = { name: '', slug: '', origins: '', webhook_url: '', scopes: ALL_SCOPES, active: true, discount: '0' };

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const LOGO_MAX = 2 * 1024 * 1024;

const slugify = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);

// PlatformPartners — cadastro dos softwares parceiros do bZapper Connect (só o dono da
// plataforma). O secret bz_partner_ e o secret do webhook aparecem UMA vez.
export function PlatformPartners() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partner | 'new' | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [secrets, setSecrets] = useState<{ title: string; items: [string, string][] } | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['platform-partners'],
    queryFn: async (): Promise<Row[]> => {
      const r = await apiFetch('GET', '/platform/partners');
      return r.ok ? ((r.data as { data?: Row[] })?.data ?? []) : [];
    },
  });
  const conns = useQuery({
    queryKey: ['platform-partner-conns', open],
    enabled: !!open,
    queryFn: async (): Promise<Conn[]> => {
      const r = await apiFetch('GET', `/platform/partners/${open}/connections`);
      return r.ok ? ((r.data as { data?: Conn[] })?.data ?? []) : [];
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['platform-partners'] });
  const copy = (v: string) => { navigator.clipboard?.writeText(v); toast.success('Copiado.'); };

  const resetLogo = () => { setLogoFile(null); setLogoPreview(''); };

  const startNew = () => { setForm(emptyForm); resetLogo(); setEditing('new'); };
  const startEdit = (p: Partner) => {
    setForm({
      name: p.name, slug: p.slug, origins: p.allowed_origins.join('\n'),
      webhook_url: p.webhook_url, scopes: p.key_scopes, active: p.active,
      discount: String(p.discount_pct ?? 0),
    });
    resetLogo();
    setEditing(p);
  };

  /** Escolha do arquivo: valida tipo e tamanho aqui e já mostra a pré-visualização. */
  const pickLogo = (file?: File) => {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) { toast.error('Use PNG, JPEG, WebP ou SVG.'); return; }
    if (file.size > LOGO_MAX) { toast.error('A imagem passa de 2 MB.'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  /** Envia a logo do parceiro (multipart). Devolve false e avisa quando falha. */
  const enviarLogo = async (partnerId: string): Promise<boolean> => {
    if (!logoFile) return true;
    const fd = new FormData();
    fd.append('file', logoFile);
    const r = await apiFetch('POST', `/platform/partners/${partnerId}/logo`, fd);
    if (!r.ok) {
      const code = (r.data as { code?: string })?.code;
      toast.error(code === 'uploads_disabled'
        ? 'Upload indisponível: o bucket de arquivos não está configurado neste ambiente.'
        : 'Não foi possível enviar a logo. O parceiro foi salvo; tente enviar de novo.');
      return false;
    }
    return true;
  };

  const save = async () => {
    const body = {
      name: form.name.trim(), webhook_url: form.webhook_url.trim(),
      allowed_origins: form.origins.split(/[\s,]+/).map((o) => o.trim()).filter(Boolean),
      key_scopes: form.scopes, active: form.active,
      discount_pct: Math.min(100, Math.max(0, Number(form.discount) || 0)),
    };
    setBusy(true);
    try {
      if (editing === 'new') {
        const r = await apiFetch('POST', '/platform/partners', { ...body, slug: form.slug || slugify(form.name) });
        if (!r.ok) { toast.error((r.data as { message?: string })?.message ?? 'Não foi possível cadastrar.'); return; }
        const d = r.data as { secret: string; webhook_secret: string; partner: Partner };
        // A logo sobe depois do cadastro: o arquivo é guardado no caminho do parceiro.
        await enviarLogo(d.partner.id);
        setSecrets({ title: `${body.name} cadastrado`, items: [['Secret do parceiro (backend)', d.secret], ['Secret do webhook', d.webhook_secret]] });
      } else if (editing) {
        const r = await apiFetch('PATCH', `/platform/partners/${editing.id}`, body);
        if (!r.ok) { toast.error((r.data as { message?: string })?.message ?? 'Não foi possível salvar.'); return; }
        const okLogo = await enviarLogo(editing.id);
        if (okLogo) toast.success('Parceiro atualizado.');
      }
      setEditing(null);
      resetLogo();
      refresh();
    } finally { setBusy(false); }
  };

  const rotate = async (p: Partner, kind: 'secret' | 'webhook') => {
    const what = kind === 'secret' ? 'o secret do parceiro' : 'o secret do webhook';
    if (!window.confirm(`Gerar novo ${what} de ${p.name}? O atual para de valer na hora.`)) return;
    const r = await apiFetch('POST', `/platform/partners/${p.id}/${kind === 'secret' ? 'rotate-secret' : 'rotate-webhook-secret'}`);
    if (!r.ok) { toast.error('Não foi possível gerar.'); return; }
    const d = r.data as { secret?: string; webhook_secret?: string };
    setSecrets({ title: `Novo ${what}`, items: [[kind === 'secret' ? 'Secret do parceiro' : 'Secret do webhook', (d.secret ?? d.webhook_secret) as string]] });
    refresh();
  };

  const statusBadge = (s: string) =>
    s === 'active' ? <Badge variant="success">ativa</Badge>
      : s === 'suspended' ? <Badge variant="destructive">suspensa</Badge>
        : s === 'revoked' ? <Badge variant="secondary">encerrada</Badge>
          : <Badge variant="warning">{s.replace('pending_', 'pendente: ')}</Badge>;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Softwares que embutem o <strong>bZapper Connect</strong>: o cliente deles contrata o Pro e conecta o WhatsApp
          sem sair de lá, e o parceiro recebe a API key autorizada pelo cliente.
        </p>
        <Button className="w-full shrink-0 sm:w-auto" onClick={startNew}><Plus className="size-4" /> Novo parceiro</Button>
      </div>

      {list.isLoading ? <Skeleton className="h-40 w-full" /> : (list.data ?? []).length === 0 ? (
        <Card><CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <Handshake className="size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Nenhum parceiro cadastrado.</p>
        </CardContent></Card>
      ) : (list.data ?? []).map(({ partner: p, stats }) => (
        <Card key={p.id}>
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="flex flex-wrap items-center gap-3">
              {p.logo_url
                ? <img src={p.logo_url} alt="" className="size-10 rounded-lg border object-contain" />
                : <span className="grid size-10 place-items-center rounded-lg bg-primary/10 text-primary"><Handshake className="size-5" /></span>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 font-medium">{p.name} {!p.active && <Badge variant="secondary">inativo</Badge>}</div>
                <div className="text-xs text-muted-foreground"><code>{p.slug}</code> · <code>{p.secret_prefix}…</code></div>
              </div>
              <div className="flex gap-2 text-center text-xs">
                <div className="rounded-lg border px-3 py-1.5"><div className="text-base font-semibold">{stats?.connections_active ?? 0}</div>ativas</div>
                <div className="rounded-lg border px-3 py-1.5"><div className="text-base font-semibold">{stats?.connections_pending ?? 0}</div>pendentes</div>
              </div>
            </div>
            <div className="grid gap-2 text-xs sm:grid-cols-2">
              <div><span className="text-muted-foreground">Origens:</span> {p.allowed_origins.length ? p.allowed_origins.join(', ') : <em className="text-destructive">nenhuma (o componente não abre)</em>}</div>
              <div className="truncate"><span className="text-muted-foreground">Webhook:</span> {p.webhook_url || <em>não configurado</em>}</div>
              <div className="sm:col-span-2"><span className="text-muted-foreground">Escopos da key:</span> {p.key_scopes.join(', ')}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => startEdit(p)}><Pencil className="size-3.5" /> Editar</Button>
              <Button size="sm" variant="outline" onClick={() => rotate(p, 'secret')}><RefreshCw className="size-3.5" /> Novo secret</Button>
              <Button size="sm" variant="outline" onClick={() => rotate(p, 'webhook')}><RefreshCw className="size-3.5" /> Novo secret do webhook</Button>
              <Button size="sm" variant="ghost" onClick={() => setOpen(open === p.id ? null : p.id)}>
                {open === p.id ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />} Conexões ({stats?.connections_total ?? 0})
              </Button>
            </div>
            {open === p.id && (
              conns.isLoading ? <Skeleton className="h-16 w-full" /> : (conns.data ?? []).length === 0
                ? <p className="text-xs text-muted-foreground">Nenhuma conexão ainda.</p>
                : (
                  <ul className="divide-y rounded-lg border text-xs">
                    {(conns.data ?? []).map((c) => (
                      <li key={c.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                        <code className="text-muted-foreground">{c.external_id}</code>
                        <span className="min-w-0 flex-1 truncate">{c.customer.company || c.customer.name} · {c.customer.email}</span>
                        {statusBadge(c.status)}
                        <span className="text-muted-foreground">{new Date(c.created_at).toLocaleDateString('pt-BR')}</span>
                      </li>
                    ))}
                  </ul>
                )
            )}
          </CardContent>
        </Card>
      ))}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Novo parceiro' : 'Editar parceiro'}</DialogTitle>
            <DialogDescription>O nome aparece para o cliente na tela de autorização.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="pname">Nome</Label>
              <Input id="pname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="bFocus" />
            </div>
            {editing === 'new' && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="pslug">Slug</Label>
                <Input id="pslug" value={form.slug} placeholder={slugify(form.name) || 'bfocus'}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Label htmlFor="plogo">Logo</Label>
              <div className="flex items-center gap-3">
                {(logoPreview || (editing !== 'new' && editing?.logo_url))
                  ? <img src={logoPreview || (editing !== 'new' ? editing?.logo_url : '')} alt="" className="size-14 rounded-lg border object-contain bg-muted" />
                  : <span className="grid size-14 place-items-center rounded-lg border bg-muted text-muted-foreground"><Handshake className="size-6" /></span>}
                <div className="flex flex-col gap-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('plogo')?.click()}>
                    <Upload className="size-3.5" /> {logoFile ? 'Trocar arquivo' : 'Escolher arquivo'}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {logoFile ? logoFile.name : 'PNG, JPEG, WebP ou SVG · até 2 MB'}
                  </span>
                </div>
              </div>
              <input id="plogo" type="file" className="hidden" accept={LOGO_TYPES.join(',')}
                onChange={(e) => pickLogo(e.target.files?.[0])} />
              <p className="text-xs text-muted-foreground">Aparece para o cliente do parceiro na tela de autorização.</p>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="porigins">Origens permitidas (uma por linha)</Label>
              <textarea id="porigins" rows={3} value={form.origins} onChange={(e) => setForm({ ...form, origins: e.target.value })}
                placeholder="https://app.bfocus.com.br"
                className="rounded-md border border-input bg-transparent px-3 py-2 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="phook">Webhook do parceiro</Label>
              <Input id="phook" value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} placeholder="https://api.bfocus.com.br/webhooks/bzapper" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="pdesc">Desconto das contas deste parceiro (%)</Label>
              <Input id="pdesc" inputMode="numeric" value={form.discount} placeholder="0"
                onChange={(e) => setForm({ ...form, discount: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
              <p className="text-xs text-muted-foreground">
                {(() => {
                  const d = Math.min(100, Math.max(0, Number(form.discount) || 0));
                  if (d === 0) return 'Preço cheio: as contas deste parceiro pagam como qualquer outra.';
                  if (d === 100) return 'Isento: a conta entra em Pro sem fatura e não vê a etapa de pagamento. A cortesia dura enquanto a parceria durar.';
                  return `As contas deste parceiro pagam ${100 - d}% do valor — o desconto aparece como linha na fatura delas.`;
                })()}
              </p>
              <p className="text-xs text-muted-foreground">
                Vale para as contas que ele já trouxe, a partir da próxima renovação. Não mexe em
                fatura já paga. Encerrar a parceria devolve as contas ao preço cheio — e, se eram
                isentas, elas seguem em Pro por 30 dias antes de cair para o Free.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Label>Escopos da key entregue</Label>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                {ALL_SCOPES.map((s) => (
                  <label key={s} className="flex items-center gap-2">
                    <input type="checkbox" className="accent-[#e0780c]" checked={form.scopes.includes(s)}
                      onChange={(e) => setForm({ ...form, scopes: e.target.checked ? [...form.scopes, s] : form.scopes.filter((x) => x !== s) })} />
                    <code>{s}</code>
                  </label>
                ))}
              </div>
            </div>
            {editing !== 'new' && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="accent-[#e0780c]" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Ativo (desligado, o componente não abre e o secret para de valer)
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} disabled={busy || !form.name.trim()}>{busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />} Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!secrets} onOpenChange={(o) => !o && setSecrets(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{secrets?.title}</DialogTitle>
            <DialogDescription>Copie agora e entregue ao parceiro por canal seguro — não será mostrado de novo.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            {secrets?.items.map(([label, value]) => (
              <div key={label} className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">{label}</span>
                <div className="flex items-center gap-2 rounded-md border bg-muted p-2">
                  <code className="flex-1 truncate text-xs">{value}</code>
                  <Button size="icon" variant="ghost" onClick={() => copy(value)}><Copy className="size-4" /></Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={() => setSecrets(null)}>Pronto</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
